import { useContext, useEffect, useRef, useState } from "react";

import { ChatUserFormData, getSourcesTrainedStatus } from "./api";
import { ConfigContext, GlobalsContext } from "./constants";
import { copy, getLocalStorageValue, getUserDataStorageKey } from "./functions";

export function useGlobalsContext() {
  const globalsContext = useContext(GlobalsContext);
  if (!globalsContext) {
    throw new Error("Ensure the component is wrapped with GlobalsProvider");
  }
  return globalsContext;
}

export function useConfigContext() {
  const configContext = useContext(ConfigContext);
  if (!configContext) {
    throw new Error("Ensure the component is wrapped with ConfigProvider");
  }
  return configContext;
}

export function useChatUserId(botInfoId?: string) {
  const { instanceName } = useConfigContext();

  const [chatUserId, setChatUserId] = useState<string | undefined>();

  useEffect(() => {
    const updateChatUserId = () => {
      if (!botInfoId) {
        // eslint-disable-next-line unicorn/no-useless-undefined
        return setChatUserId(undefined);
      }
      const savedUser = getLocalStorageValue(
        getUserDataStorageKey(instanceName, botInfoId),
      );
      if (savedUser) {
        try {
          const identifiedUser: ChatUserFormData = JSON.parse(savedUser);
          if (identifiedUser) {
            setChatUserId(identifiedUser?._id);
          }
        } catch {
          // Do nothing
        }
      }
    };

    updateChatUserId();

    const handleChatUserSet = () => {
      updateChatUserId();
    };

    window.addEventListener("CHAT_USER_SET", handleChatUserSet);

    return () => {
      window.removeEventListener("CHAT_USER_SET", handleChatUserSet);
    };
  }, [instanceName, botInfoId]);

  return chatUserId;
}

export function useResizeObserver(
  targetRef: React.MutableRefObject<HTMLElement | null>,
) {
  const [contentRect, setContentRect] = useState({});
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const disconnect = () => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
  };

  useEffect(() => {
    if (!("ResizeObserver" in window)) {
      return;
    }

    disconnect();

    resizeObserverRef.current = new ResizeObserver(entries => {
      if (entries) {
        setContentRect(() => copy(entries[0].contentRect));
      }
    });
    if (targetRef.current) {
      resizeObserverRef.current.observe(targetRef.current);
    }

    return disconnect;
  }, [targetRef]);

  return contentRect;
}

/**
 * Checks if all Uploaded data is trained
 * Data checked
 * - Bot Data (Link, File, Spreadsheet)
 * - Bot FAQ's
 * - Bot Sitemaps
 *
 * @returns {boolean} isBotDataCompletelyTrained
 */
export function useIsBotDataCompletelyTrained(botId?: string, token?: string) {
  const [isBotDataCompletelyTrained, setIsBotDataCompletelyTrained] =
    useState<boolean>(true);
  useEffect(() => {
    if (!botId || !token) {
      return;
    }
    const savedBotData = () => {
      getSourcesTrainedStatus({
        botId,
        token,
      }).then(data => {
        setIsBotDataCompletelyTrained(data.is_all_data_trained);
      });
    };
    savedBotData();
  }, [botId, token]);

  return isBotDataCompletelyTrained;
}
